
import React, { useEffect } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SermonHeader } from "@/components/sermon-plans/SermonHeader";
import { SermonPlanForm } from "@/components/sermon-plans/form/SermonPlanForm";
import { WelcomeDialog } from "@/components/sermon-plans/welcome/WelcomeDialog";
import { AISermonDialog } from "@/components/sermon-plans/AISermonDialog";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { AuthenticationRequired } from "@/components/sermon-plans/AuthenticationRequired";
import { EmptySermonPlans } from "@/components/sermon-plans/EmptySermonPlans";
import { SermonPlansLoading } from "@/components/sermon-plans/SermonPlansLoading";
import { SermonPlansList } from "@/components/sermon-plans/SermonPlansList";
import { useSermonPlansPage } from "@/hooks/sermon-plans/use-sermon-plans-page";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";

export default function SermonPlansPage() {
  const { toast } = useToast();
  const {
    sermonPlans,
    congregations,
    loading,
    isAuthenticated,
    isOffline,
    fetchError,
    nextAutoGenerationDate,
    formOpen,
    currentSermonPlan,
    showWelcomeDialog,
    showAIDialog,
    activeTab,
    setFormOpen,
    setShowWelcomeDialog,
    setShowAIDialog,
    setActiveTab,
    handleOpenForm,
    handleFormSubmit,
    handleDelete,
    handleCreateFirstSermon,
    generateAISermon,
    runAutoGeneration,
    fetchData
  } = useSermonPlansPage();

  // Handle authentication status
  if (isAuthenticated === false) {
    return <AuthenticationRequired />;
  }

  // Ensure page scrolls to top on mount and after data operations
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [loading]);

  // Enhanced create sermon handler that shows toast feedback
  const handleCreateWithFeedback = async (formData) => {
    try {
      await handleCreateFirstSermon(formData);
      toast({
        title: "Success",
        description: "Sermon plan created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create sermon plan",
        variant: "destructive",
      });
    }
  };

  const handleRetry = () => {
    toast({
      title: "Retrying",
      description: "Attempting to reconnect and fetch data..."
    });
    fetchData();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <ScrollToTop />
      
      <main className="container mx-auto px-4 py-6 pb-36 flex-grow">
        <SermonHeader 
          nextAutoGenerationDate={nextAutoGenerationDate}
          onAddNew={() => handleOpenForm()}
          onGenerate={() => {
            runAutoGeneration();
            toast({
              title: "Generating sermon plans",
              description: "AI is generating new sermon plans based on congregation data"
            });
          }}
        />

        {isOffline && (
          <div className="flex items-center justify-center py-4 mb-4">
            <Button 
              onClick={handleRetry} 
              variant="outline" 
              className="flex items-center gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              Reconnect
            </Button>
          </div>
        )}

        {loading ? (
          <SermonPlansLoading 
            isOffline={isOffline}
            error={fetchError}
            onRetry={handleRetry}
          />
        ) : sermonPlans.length === 0 ? (
          <EmptySermonPlans onOpenWelcome={() => setShowWelcomeDialog(true)} />
        ) : (
          <SermonPlansList 
            sermonPlans={sermonPlans}
            onEdit={handleOpenForm}
            onDelete={handleDelete}
          />
        )}
      </main>

      <WelcomeDialog
        open={showWelcomeDialog}
        onOpenChange={setShowWelcomeDialog}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onCreateSermon={handleCreateWithFeedback}
      />
      
      <AISermonDialog
        open={showAIDialog}
        onOpenChange={setShowAIDialog}
        onGenerate={generateAISermon}
      />

      <SermonPlanForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleFormSubmit}
        currentSermonPlan={currentSermonPlan}
        congregations={congregations}
      />

      <AdminNavigation />
    </div>
  );
}
